import React, { useEffect, useState, useRef } from 'react';
import { adminApi } from '../api/client';
import './css/admin-announcements.css';

const CATEGORIES = ['updates', 'events', 'roadmap', 'community'];

const EMPTY = {
  title: '',
  content: '',
  preview_text: '',
  category: 'updates',
  status: 'draft',
  publish_date: '',
  sort_order: 0,
  image_url: '',
};

function toLocalDatetime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const imageRef = useRef(null);

  const load = () => {
    adminApi.getAnnouncements().then(setItems).catch((e) => setError(e.message));
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    if (imageRef.current) imageRef.current.value = '';
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      content: item.content,
      preview_text: item.preview_text || '',
      category: item.category || 'updates',
      status: item.status,
      publish_date: toLocalDatetime(item.publish_date),
      sort_order: item.sort_order || 0,
      image_url: item.image_url || '',
    });
    if (imageRef.current) imageRef.current.value = '';
  };

  const buildPayload = () => {
    const file = imageRef.current?.files?.[0];
    if (file) {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('content', form.content);
      fd.append('preview_text', form.preview_text || form.content.slice(0, 160));
      fd.append('category', form.category);
      fd.append('status', form.status);
      fd.append('sort_order', String(form.sort_order));
      if (form.publish_date) fd.append('publish_date', new Date(form.publish_date).toISOString());
      fd.append('image', file);
      return fd;
    }
    return {
      title: form.title,
      content: form.content,
      preview_text: form.preview_text || form.content.slice(0, 160),
      category: form.category,
      status: form.status,
      sort_order: form.sort_order,
      image_url: form.image_url || null,
      publish_date: form.publish_date ? new Date(form.publish_date).toISOString() : undefined,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      if (editingId) {
        await adminApi.updateAnnouncement(editingId, payload);
      } else {
        await adminApi.createAnnouncement(payload);
      }
      resetForm();
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await adminApi.deleteAnnouncement(id);
      if (editingId === id) resetForm();
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page admin-page--announcements">
      <header className="admin-page-header">
        <h1>Announcements</h1>
        <p>Create and publish official updates for the public announcements page.</p>
      </header>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-two-col">
        <form className="admin-card admin-form" onSubmit={handleSubmit}>
          <h2>{editingId ? 'Edit Announcement' : 'New Announcement'}</h2>

          <label className="admin-label">
            Title
            <input className="admin-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </label>

          <label className="admin-label">
            Full Content
            <textarea className="admin-textarea" rows={5} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required />
          </label>

          <label className="admin-label">
            Preview Text
            <textarea className="admin-textarea" rows={2} value={form.preview_text} onChange={(e) => setForm({ ...form, preview_text: e.target.value })} placeholder="Short summary shown on cards (auto-generated if empty)" />
          </label>

          <div className="admin-form-row">
            <label className="admin-label">
              Category
              <select className="admin-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>

            <label className="admin-label">
              Status
              <select className="admin-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </label>
          </div>

          <div className="admin-form-row">
            <label className="admin-label">
              Publish Date
              <input className="admin-input" type="datetime-local" value={form.publish_date} onChange={(e) => setForm({ ...form, publish_date: e.target.value })} />
            </label>

            <label className="admin-label">
              Sort Order
              <input className="admin-input" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </label>
          </div>

          <label className="admin-label">
            Cover Image (optional)
            <input ref={imageRef} className="admin-file-input" type="file" accept="image/*" />
          </label>

          {form.image_url && !imageRef.current?.files?.[0] && (
            <div className="admin-image-preview">
              <img src={form.image_url} alt="Current cover" />
            </div>
          )}

          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
            {editingId && (
              <button type="button" className="admin-btn admin-btn-ghost" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </form>

        <div className="admin-list">
          {items.map((item) => (
            <article key={item.id} className="admin-list-card">
              <div className="admin-list-card-header">
                <h3>{item.title}</h3>
                <span className={`admin-badge admin-badge-${item.status}`}>{item.status}</span>
              </div>
              {item.image_url && (
                <img src={item.image_url} alt="" className="admin-list-thumb" />
              )}
              <p className="admin-list-card-body">{item.preview_text}</p>
              <p className="admin-list-meta">
                {item.category} · {item.publish_date ? new Date(item.publish_date).toLocaleDateString() : 'No date'}
              </p>
              <div className="admin-list-actions">
                <button type="button" className="admin-btn admin-btn-sm" onClick={() => handleEdit(item)}>Edit</button>
                <button type="button" className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => handleDelete(item.id)}>Delete</button>
              </div>
            </article>
          ))}
          {!items.length && <p className="admin-empty">No announcements yet.</p>}
        </div>
      </div>
    </div>
  );
}
